import { useEffect, useRef } from 'react';

/** How often the countdown is read, not how long it is: fine enough to look immediate. */
const IDLE_CHECK_MS = 250;

const listenForActivity = (onActivity: () => void): (() => void) => {
  const controller = new AbortController();
  const options = { passive: true, signal: controller.signal };

  addEventListener('pointerdown', onActivity, options);
  addEventListener('pointermove', onActivity, options);
  addEventListener('keydown', onActivity, options);
  addEventListener('touchstart', onActivity, options);
  addEventListener('input', onActivity, options);

  return (): void => {
    controller.abort();
  };
};

type IdleDismissOptions = {
  readonly dismissAfterMs: number;
  readonly onDismiss: () => void;
};

/**
 * A surface nobody is using: `onDismiss` is called once it has been idle for
 * `dismissAfterMs`. Typing, a pointer, a key or a touch anywhere on the page
 * counts as use and puts the countdown back to the start.
 *
 * Activity is deliberately kept out of React state — a pointer crossing the
 * page would otherwise re-render everything under it.
 */
export const useIdleDismiss = ({ dismissAfterMs, onDismiss }: IdleDismissOptions): void => {
  const draftLastActivityAt = useRef(0);
  const draftOnDismiss = useRef(onDismiss);

  useEffect(() => {
    draftOnDismiss.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    draftLastActivityAt.current = Date.now();

    const stopListening = listenForActivity(() => {
      draftLastActivityAt.current = Date.now();
    });

    const timer = setInterval(() => {
      const idleMs = Date.now() - draftLastActivityAt.current;
      if (idleMs >= dismissAfterMs) {
        draftOnDismiss.current();
      }
    }, IDLE_CHECK_MS);

    return (): void => {
      clearInterval(timer);
      stopListening();
    };
  }, [dismissAfterMs]);
};
